import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { ArrowLeft, Package, Truck, TrendingDown, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/banner-agriculture.jpg";

const Professionisti = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Servizi per Professionisti dell'Agricoltura"
        description="Servizi per professionisti del settore agricolo e zootecnico: forniture all'ingrosso, trasporto e consegna programmata in zona, prezzi competitivi. A Magione (PG)."
        canonical="/professionisti"
      />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Professionisti", url: "/professionisti" }
      ]} />
      <Navbar />

      {/* Hero Banner Section */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/#prodotti" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai prodotti
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Package className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Soluzioni per Professionisti
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Prodotti professionali per vivaisti, allevatori, addestratori, cacciatori e giardinieri.
              Disponibilità immediata e su ordinazione, anche per grandi quantità.
            </p>
          </div>
        </div>
      </section>

      {/* Categorie Professionali */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Le Nostre Categorie Professionali
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Offriamo una vasta gamma di prodotti specifici per ogni settore professionale
            </p>
          </div>

          {/* Card categorie */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Terriccio Professionale */}
            <div className="bg-card rounded-2xl p-8 border border-border card-hover">
              <h3 className="text-xl font-heading font-semibold mb-4">
                Terriccio Professionale
              </h3>
              <p className="text-muted-foreground mb-6">
                Balloni di terriccio di alta qualità per uso professionale
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Big bag di terriccio su ordinazione</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Terricci specifici per ogni coltura</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Torba professionale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Substrati tecnici per vivaisti</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Consegna anche per grandi quantità</span>
                </li>
              </ul>
            </div>

            {/* Cura del Prato Professionale */}
            <div className="bg-card rounded-2xl p-8 border border-border card-hover">
              <h3 className="text-xl font-heading font-semibold mb-4">
                Cura del Prato Professionale
              </h3>
              <p className="text-muted-foreground mb-6">
                Prodotti professionali per manutenzione tappeti erbosi
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Concimi granulari per prati sportivi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Sementi professionali per tappeti erbosi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Antimuschio e diserbanti selettivi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Attrezzature per manutenzione prati</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Consulenza tecnica specializzata</span>
                </li>
              </ul>
            </div>

            {/* Mangimi per Allevamenti */}
            <div className="bg-card rounded-2xl p-8 border border-border card-hover">
              <h3 className="text-xl font-heading font-semibold mb-4">
                Mangimi per Allevamenti
              </h3>
              <p className="text-muted-foreground mb-6">
                Mangimi e integratori per allevamenti di ogni dimensione
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Mangimi per bovini, suini, ovini</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Farine e integratori zootecnici</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Mangimi biologici certificati</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Consegna per grandi quantità</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Sconti per grandi acquisti</span>
                </li>
              </ul>
            </div>

            {/* Articoli per Cacciatori */}
            <div className="bg-card rounded-2xl p-8 border border-border card-hover">
              <h3 className="text-xl font-heading font-semibold mb-4">
                Articoli per Cacciatori
              </h3>
              <p className="text-muted-foreground mb-6">
                Prodotti specifici per cani da caccia e attività venatoria
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Mangimi energetici per cani da caccia</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Integratori per resistenza</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Accessori e attrezzature</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2 flex-shrink-0">✓</span>
                  <span className="text-sm">Prodotti per la cura del cane</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Perché Sceglierci */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Perché Sceglierci
            </h2>
            <p className="text-lg text-muted-foreground">
              Vantaggi dedicati ai professionisti del settore
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <Package className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">
                Prodotti in Negozio e su Ordinazione
              </h3>
              <p className="text-muted-foreground">
                Ampia disponibilità di prodotti a magazzino e possibilità di ordinare anche articoli speciali e grandi quantità
              </p>
            </div>

            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <Truck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">
                Consegna anche per Grandi Volumi
              </h3>
              <p className="text-muted-foreground">
                Gestiamo ordini di qualsiasi dimensione, anche prodotti molto voluminosi come balloni di terriccio e bancali di mangimi
              </p>
            </div>

            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <TrendingDown className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">
                Sconti su Grandi Acquisti
              </h3>
              <p className="text-muted-foreground">
                Prezzi competitivi e scontistica dedicata per ordini importanti e clienti abituali
              </p>
            </div>

            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">
                Qualità Garantita
              </h3>
              <p className="text-muted-foreground">
                Selezioniamo solo i migliori marchi e prodotti certificati per garantire risultati professionali
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prezzi Competitivi */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Prezzi Competitivi per Professionisti
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Offriamo prezzi vantaggiosi per grandi acquisti e condizioni dedicate per clienti professionali. Richiedi un preventivo personalizzato senza impegno.
          </p>
          <Link
            to="/#contatti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Contattaci
          </Link>
        </div>
      </section>

      {/* Mappa */}
      <ContactSection />

      <Footer />
    </div>
  );
};

export default Professionisti;
